import { useEffect, useState } from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { IconBellRinging } from "@tabler/icons";
import { isPushSupported, enablePushNotifications } from "../services/PushNotifications";
import { ShowNotificationError } from "./Notifications";

const DISMISSED_KEY = "pushNotificationBannerDismissed";

// Prompts enabling desktop notifications for new Posts - currently only rendered on the
// Dashboard, since that's where Posts themselves show up. Hidden for good once dismissed,
// successfully enabled, or the browser's own permission has already been explicitly granted or
// denied by the time this mounts - there's nothing useful left for this banner to offer once
// Notification.permission is anything other than its initial "default" state (a "denied" browser
// permission can only be changed back via that browser's own site settings, not re-prompted from
// here, so nagging about it would be pointless).
export default function NotificationPermissionBanner({ userToken, companyId }) {
  const [visible, setVisible] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    isPushSupported().then((supported) => {
      if (cancelled || !supported) return;
      const dismissed = window.localStorage.getItem(DISMISSED_KEY) === "true";
      setVisible(!dismissed && Notification.permission === "default");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(DISMISSED_KEY, "true");
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <Alert icon={<IconBellRinging size={18} />} color="blue" variant="light" withCloseButton onClose={dismiss} mb="md">
      <Group position="apart" spacing="sm" noWrap>
        <Text size="sm">Turn on desktop notifications to hear about new posts as soon as they're published.</Text>
        <Button
          size="xs"
          loading={isEnabling}
          sx={{ flexShrink: 0 }}
          onClick={async () => {
            setIsEnabling(true);
            try {
              await enablePushNotifications({ userIdToken: userToken, companyId });
            } catch (err) {
              console.error(err);
              ShowNotificationError("Couldn't enable notifications", "Please try again.");
              setIsEnabling(false);
              return;
            }
            // Dismissed either way once acted on - if the browser's permission prompt was denied
            // rather than granted, Notification.permission is now "denied" and can't be
            // re-prompted from this banner again regardless, so there's nothing left to show for.
            setIsEnabling(false);
            dismiss();
          }}
        >
          Enable notifications
        </Button>
      </Group>
    </Alert>
  );
}
